/**
 * Cart Payload Builders
 * 
 * Functions for building cart payloads for different artwork methods.
 * Handles: Start New Imprint, Send Us Artwork, Use Previous Artwork
 */

import { 
    calculateAllFees, 
    getUniqueColorsCount, 
    getUniqueCustomColorsCount,
    hasUploadedImage,
    combineProofFee,
    getFeeUnit
} from './feeCalculator.js';
import { useSelectionStore } from '../stores/useSelectionStore.js';
import { useAppStore } from '../stores/useAppStore.js';

/**
 * Build Send Us Artwork payload
 * 
 * Features:
 * - Empty imprint_lines
 * - Location + method in properties
 * - Special instruction: "Send Us Your Artwork After Ordering"
 * - Fees: from backend config (setup_fee, proof_fee)
 * 
 * @param {Object} imprint - Current imprint from newImprintStore
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} priceData - Price data from gatherPriceData()
 * @param {Object} backendConfig - Backend fee configuration (optional)
 * @returns {Object} Payload with imprint_lines and properties
 */
export function buildSendArtworkPayload(imprint, imprintStore, priceData, backendConfig = {}) {
    console.log('[PayloadBuilder] buildSendArtworkPayload()', { backendConfig });
    
    // Check if this is an Envelop product (no locations)
    const appStore = useAppStore();
    const isEnvelopProduct = appStore.isEnvelopProduct;
    
    // Get location data (required even for Send Artwork, except for Envelop products)
    const locationTitle = imprint?.selectedLocation?.title || '';
    const locationSku = imprint?.selectedLocation?.sku || '';
    
    // Build special instruction with comment
    const baseInstruction = "Send Us Your Artwork After Ordering.";
    const userComment = imprint?.specialInstructions || '';
    const globalSpecialInstructions = getSpecialInstructions();
    const imprintDetails = `${baseInstruction} \r\n\r\nComment: ${userComment}`;
    
    // Calculate fees for Send Artwork with backend config
    const fees = calculateAllFees({
        uniqueColorsCount: 1, // Assume 1 color for send artwork
        totalQuantity: 0,
        hasPantoneColor: false,
        hasImage: false,
        artworkMethod: 'send',
        backendConfig: backendConfig  // ✅ Pass backend config
    });
    
    // Update priceData with calculated fees
    const updatedPriceData = {
        ...priceData,
        setup_fee: fees.setup_fee,
        proof_fee: fees.proof_fee,
        running_fee: fees.running_fee,
        customColourFee: fees.customColourFee,
        image_fee: fees.image_fee
    };
    
    // Build properties
    const properties = {
        "Imprint Method": imprintStore.selectedImprintMethod?.name || '',
        "_Imprint Method SKU": imprintStore.selectedImprintMethod?.sku || '',
        
        "Imprint Details": imprintDetails,
        "Special Instruction": globalSpecialInstructions,
        
        "_price_details": updatedPriceData,
        "_colorUpcharge": imprintStore.colorUpcharge || {},
        "_sizeUpcharge": imprintStore.sizeUpcharge || {}
    };
    
    // Only add location data for non-Envelop products
    // Envelop products do not have location selection
    if (!isEnvelopProduct) {
        properties["Imprint location"] = locationTitle;
        properties["_Imprint Location SKU"] = locationSku;
    }
    
    console.log('[PayloadBuilder] Send Artwork fees:', {
        setup_fee: fees.setup_fee,
        proof_fee: fees.proof_fee
    });
    
    return {
        imprint_lines: {},
        properties: properties,
        fees: fees // Return fees for runningFeePerItem
    };
}

/**
 * Build Use Previous Artwork payload
 * 
 * Features:
 * - Empty imprint_lines
 * - Location + method in properties
 * - Special instruction: "Use My Previous Artwork"
 * - Fees: setup_fee: $0, proof_fee: $0 (already paid, regardless of backend config)
 * 
 * @param {Object} imprint - Current imprint from newImprintStore
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} priceData - Price data from gatherPriceData()
 * @param {Object} backendConfig - Backend fee configuration (optional, not used for previous)
 * @returns {Object} Payload with imprint_lines and properties
 */
export function buildPreviousArtworkPayload(imprint, imprintStore, priceData, backendConfig = {}) {
    console.log('[PayloadBuilder] buildPreviousArtworkPayload()', { backendConfig });
    
    // Check if this is an Envelop product (no locations)
    const appStore = useAppStore();
    const isEnvelopProduct = appStore.isEnvelopProduct;
    
    // Get location data (required even for Previous Artwork, except for Envelop products)
    const locationTitle = imprint?.selectedLocation?.title || '';
    const locationSku = imprint?.selectedLocation?.sku || '';
    
    // Build special instruction with comment
    const baseInstruction = "Use My Previous Artwork.";
    const userComment = imprint?.specialInstructions || '';
    const globalSpecialInstructions = getSpecialInstructions();
    const imprintDetails = `${baseInstruction} \r\n\r\nComment: ${userComment}`;
    
    // Calculate fees for Previous Artwork (should be $0 regardless of backend config)
    const fees = calculateAllFees({
        uniqueColorsCount: 0,
        totalQuantity: 0,
        hasPantoneColor: false,
        hasImage: false,
        artworkMethod: 'previous',
        backendConfig: backendConfig  // ✅ Pass backend config (though not used for 'previous')
    });
    
    // Update priceData with calculated fees
    const updatedPriceData = {
        ...priceData,
        setup_fee: fees.setup_fee,
        proof_fee: fees.proof_fee,
        running_fee: fees.running_fee,
        customColourFee: fees.customColourFee,
        image_fee: fees.image_fee
    };
    
    // Build properties
    const properties = {
        "Imprint Method": imprintStore.selectedImprintMethod?.name || '',
        "_Imprint Method SKU": imprintStore.selectedImprintMethod?.sku || '',
        
        "Imprint Details": imprintDetails,
        "Special Instruction": globalSpecialInstructions,
        
        "_price_details": updatedPriceData,
        "_colorUpcharge": imprintStore.colorUpcharge || {},
        "_sizeUpcharge": imprintStore.sizeUpcharge || {}
    };
    
    // Only add location data for non-Envelop products
    // Envelop products do not have location selection
    if (!isEnvelopProduct) {
        properties["Imprint location"] = locationTitle;
        properties["_Imprint Location SKU"] = locationSku;
    }
    
    console.log('[PayloadBuilder] Previous Artwork fees:', {
        setup_fee: fees.setup_fee,
        proof_fee: fees.proof_fee
    });
    
    return {
        imprint_lines: {},
        properties: properties,
        fees: fees // Return fees for runningFeePerItem
    };
}

/**
 * Format line items fee properties for cart
 * 
 * Converts backend fee format to Shopify cart properties format.
 * SKUs are read from product data (imprint_data) for non-zero fees.
 * 
 * @param {Object} lineItemsFeeProperties - Fee properties from backend
 * @param {string} groupID - Current group ID
 * @param {Object} feeSkus - Fee SKUs from product imprint_data
 * @param {string} feeSkus.setup_fee_sku
 * @param {string} feeSkus.running_fee_sku
 * @param {string} feeSkus.image_fee_sku
 * @param {string} feeSkus.colour_fee_sku
 * @param {string} feeSkus.proof_fee_sku
 * @returns {Object} Formatted fee properties
 */
export function formatLineItemsFeeForCart(lineItemsFeeProperties, groupID, feeSkus = {}, priceDetails = {}) {
    const feeFormatted = {};
    const customColourLabel = window.Shopify?.country === 'CA' ? 'Custom Colour Fee' : 'Custom Color Fee';
    
    console.log('[PayloadBuilder] formatLineItemsFeeForCart - input:', lineItemsFeeProperties, 'feeSkus:', feeSkus, 'priceDetails:', priceDetails);
    
    if (typeof lineItemsFeeProperties === 'object') {
        for (const [key, value] of Object.entries(lineItemsFeeProperties)) {
            const feeValue = Number(value['value']);
            const hasValue = feeValue > 0;
            
            switch(value['name']) {
                case 'setup_fee': {
                    const setupQty = Number(priceDetails.setup_fee_qty);
                    const setupUnitPrice = Number(priceDetails.setup_fee_unit_price);
                    if (setupQty >= 1 && setupUnitPrice > 0) {
                        feeFormatted['Setup Fee Qty'] = String(setupQty);
                        feeFormatted['Setup Fee Unit Price'] = setupUnitPrice.toFixed(2);
                        feeFormatted['Setup Fee Subtotal'] = feeValue.toFixed(2);
                    } else {
                        feeFormatted['Setup Fee'] = feeValue.toFixed(2);
                    }
                    if (hasValue && feeSkus.setup_fee_sku) {
                        feeFormatted['_Setup Fee SKU'] = feeSkus.setup_fee_sku;
                    }
                    break;
                }
                case 'proof_fee': {
                    const proofRate = Number(priceDetails.proof_fee_unit_price) || feeValue;
                    if (hasValue) {
                        feeFormatted['Proof Fee Qty'] = '1';
                        feeFormatted['Proof Fee Unit Price'] = proofRate.toFixed(2);
                        feeFormatted['Proof Fee Subtotal'] = feeValue.toFixed(2);
                    } else {
                        feeFormatted['Proof Fee'] = feeValue.toFixed(2);
                    }
                    if (hasValue && feeSkus.proof_fee_sku) {
                        feeFormatted['_Proof Fee SKU'] = feeSkus.proof_fee_sku;
                    }
                    break;
                }
                case 'image_fee': {
                    const imgQty = Number(priceDetails.image_fee_qty);
                    const imgUnitPrice = Number(priceDetails.image_fee_unit_price) || feeValue;
                    if (hasValue) {
                        feeFormatted['Image Fee Qty'] = String(imgQty >= 1 ? imgQty : 1);
                        feeFormatted['Image Fee Unit Price'] = imgUnitPrice.toFixed(2);
                        feeFormatted['Image Fee Subtotal'] = feeValue.toFixed(2);
                    } else {
                        feeFormatted['Image Fee'] = feeValue.toFixed(2);
                    }
                    if (hasValue && feeSkus.image_fee_sku) {
                        feeFormatted['_Image Fee SKU'] = feeSkus.image_fee_sku;
                    }
                    break;
                }
                case 'running_fee': {
                    const runQty = Number(priceDetails.running_fee_qty);
                    const runUnitPrice = Number(priceDetails.running_fee_unit_price);
                    if (runQty > 0 && runUnitPrice > 0) {
                        feeFormatted['Running Fee Qty'] = String(runQty);
                        feeFormatted['Running Fee Unit Price'] = runUnitPrice.toFixed(2);
                        feeFormatted['Running Fee Subtotal'] = feeValue.toFixed(2);
                    } else {
                        feeFormatted['Running Fee'] = feeValue.toFixed(2);
                    }
                    if (hasValue && feeSkus.running_fee_sku) {
                        feeFormatted['_Running Fee SKU'] = feeSkus.running_fee_sku;
                    }
                    break;
                }
                case 'custom_colour_fee': {
                    const customQty = Number(priceDetails.customColourFee_qty);
                    const customUnitPrice = Number(priceDetails.customColourFee_unit_price);
                    if (customQty >= 1 && customUnitPrice > 0) {
                        feeFormatted[`${customColourLabel} Qty`] = String(customQty);
                        feeFormatted[`${customColourLabel} Unit Price`] = customUnitPrice.toFixed(2);
                        feeFormatted[`${customColourLabel} Subtotal`] = feeValue.toFixed(2);
                    } else {
                        feeFormatted[customColourLabel] = feeValue.toFixed(2);
                    }
                    if (hasValue && feeSkus.colour_fee_sku) {
                        feeFormatted[`_${customColourLabel} SKU`] = feeSkus.colour_fee_sku;
                    }
                    break;
                }
                case 'additional_location_fee': {
                    const addLocQty = Number(priceDetails.additional_location_fee_qty);
                    const addLocUnitPrice = Number(priceDetails.additional_location_fee_unit_price);
                    if (addLocQty >= 1 && addLocUnitPrice > 0) {
                        feeFormatted['Additional Location Fee Qty'] = String(addLocQty);
                        feeFormatted['Additional Location Fee Unit Price'] = addLocUnitPrice.toFixed(2);
                        feeFormatted['Additional Location Fee Subtotal'] = feeValue.toFixed(2);
                    } else {
                        feeFormatted['Additional Location Fee'] = feeValue.toFixed(2);
                    }
                    if (hasValue && feeSkus.additional_location_fee_sku) {
                        feeFormatted['_Additional Location Fee SKU'] = feeSkus.additional_location_fee_sku;
                    }
                    break;
                }
                default:
                    break;
            }
        }
    }
    
    feeFormatted["_groupID"] = groupID;
    
    console.log('[PayloadBuilder] formatLineItemsFeeForCart - output:', feeFormatted);
    
    return feeFormatted;
}

/**
 * Build cart payload for calendar products
 * 
 * Calendar mode differences:
 * - No colors/sizes - each calendar is a separate variant
 * - Products come from localStorage (calendarStore)
 * - Each calendar gets 'Is Calendar': 'Yes' property
 * - All calendars linked via '_groupedCalendarId'
 * 
 * @param {Object} appStore - App store instance
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} newImprintStore - New imprint store instance
 * @param {Object} calendarStore - Calendar store instance
 * @param {Object} imprintDetails - Imprint details from buildImprintDetails
 * @returns {Object} Calendar cart payload
 */
export function buildCalendarCartPayload(appStore, imprintStore, newImprintStore, calendarStore, imprintPayload) {
    console.log('[PayloadBuilder] Building calendar cart payload');
    console.log('[PayloadBuilder] Received imprintPayload:', imprintPayload);
    
    // Extract properties and imprint_lines from full payload
    // imprintPayload can be either:
    // 1. Full payload with { properties, imprint_lines, fees } (from combineMultipleImprintsAsArray)
    // 2. Just properties object (legacy/fallback)
    const imprintProperties = imprintPayload?.properties || imprintPayload || {};
    const imprintLines = imprintPayload?.imprint_lines || {};
    
    // Get total quantity from calendar store (NOT selection store!)
    const totalQty = calendarStore.totalQuantity;
    console.log('[PayloadBuilder] Calendar totalQty:', totalQty);
    
    // Get backend fee configuration
    const sku = imprintStore.selectedImprintMethod?.sku;
    let backendConfig = {};
    
    if (sku && imprintStore.fullJson?.configurable_array?.[sku]) {
        const imprintData = imprintStore.fullJson.configurable_array[sku].configurable_2_data?.imprint_data;
        if (imprintData) {
            backendConfig = {
                setup_fee: imprintData.setup_fee || 0,
                running_fee: imprintData.running_fee || 0,
                custom_colour_fee: imprintData.custom_colour_fee || 0,
                image_fee: imprintData.image_fee || 0,
                proof_fee: imprintData.proof_fee || 0
            };
        }
    }
    
    console.log('[PayloadBuilder] Calendar backend fee config:', backendConfig);
    
    // Check if COMDA store
    const isComda = window.Shopify?.shop?.includes('comda') ?? true;
    const artworkMethod = imprintStore.selectedArtworkMethod || 'new';
    
    // ✅ Calculate fees for EACH imprint and SUM them (like ReviewSection does)
    const allImprints = newImprintStore.imprints;
    let totalSetupFee = 0;
    let totalProofFee = 0;
    let totalRunningFee = 0;
    let totalImageFee = 0;
    let totalCustomColorFee = 0;
    const calendarImprintsFees = []; // collects fee object from each imprint for combiners
    
    console.log('[PayloadBuilder] Calculating fees for', allImprints.length, 'calendar imprints');
    
    allImprints.forEach((imprint, index) => {
        const uniqueColorsCount = getUniqueColorsCount(imprint);
        const uniqueCustomColorsCount = getUniqueCustomColorsCount(imprint);
        
        const imageDataExists = hasUploadedImage(imprint);
        const messageType = imprint?.messageType || 'text';
        const messageTypeAllowsImage = messageType === 'image' || messageType === 'text_image';
        const hasImage = imageDataExists && messageTypeAllowsImage;
        
        // Calculate fees for this imprint with CORRECT totalQty from calendarStore
        const fees = calculateAllFees({
            uniqueColorsCount,
            totalQuantity: totalQty,  // ✅ FIX: Use calendar totalQty, not 1
            hasPantoneColor: uniqueCustomColorsCount,
            hasImage: hasImage,
            artworkMethod,
            isComda,
            backendConfig
        });
        
        console.log(`[PayloadBuilder] Calendar imprint ${index} fees:`, {
            uniqueColorsCount,
            uniqueCustomColorsCount,
            hasImage,
            totalQty,
            fees
        });
        
        totalSetupFee += fees.setup_fee || 0;
        // Collect per-imprint fees for combiners
        calendarImprintsFees.push(fees);
        totalRunningFee += fees.running_fee || 0;
        totalImageFee += fees.image_fee || 0;
        totalCustomColorFee += fees.customColourFee || 0;
    });

    // Read fee_unit for proof fee from product fees array
    const feesArray = imprintStore.fullJson?.configurable_array?.[sku]?.fees || [];
    const proofFeeUnit = getFeeUnit(feesArray, 'PROOF', 'per order');

    // Proof fee — per-order logic via combiner
    totalProofFee = combineProofFee(calendarImprintsFees, proofFeeUnit);
    
    console.log('[PayloadBuilder] Calendar total fees:', {
        totalSetupFee,
        totalProofFee,
        totalRunningFee,
        totalImageFee,
        totalCustomColorFee
    });
    
    // ✅ Build corrected _price_details with proper fees
    const correctedPriceDetails = {
        ...(imprintProperties._price_details || {}),
        setup_fee: totalSetupFee,
        proof_fee: totalProofFee,
        running_fee: totalRunningFee,
        image_fee: totalImageFee,
        customColourFee: totalCustomColorFee
    };
    
    const items = [];
    
    // ✅ Get groupID from appStore (generate if not exists)
    if (!appStore.currentGroupID) {
        appStore.generateGroupID();
    }
    const groupID = appStore.currentGroupID;
    
    // ✅ Get qtyIncrement from imprintStore
    const qtyIncrement = imprintStore.currentQtyIncrement || 1;
    
    console.log('[PayloadBuilder] Calendar groupID:', groupID);
    console.log('[PayloadBuilder] Calendar qtyIncrement:', qtyIncrement);
    
    // Get calendar_sku mapping from fullJson.simple_attributes (key = title, value = sku)
    const calendarSkuMap = imprintStore.fullJson?.simple_attributes?.calendar_sku || {};
    
    console.log('[PayloadBuilder] Calendar SKU map:', calendarSkuMap);

    // Loop through calendar products
    calendarStore.productsWithQuantities.forEach(calendar => {
        // Get calendar SKU from simple_attributes.calendar_sku by title
        const calendarSku = calendarSkuMap[calendar.title] || '';

        // Build properties for this calendar
        const properties = {
            'Is Calendar': 'Yes',
            'Calendar SKU': calendarSku,
            'Imprint Method': imprintStore.selectedImprintMethod?.name || '',
            '_variant_title': calendar.title,
            '_variant_title_img': calendar.image || '',
            '_Product name': calendar.title,
            '_Grouped Product name': appStore.full_product_json?.name || '',
            '_img_src': calendar.image || '',
            '_Product SKU': calendarSku,
            '_Product Weight': imprintStore.fullJson?.configurable_array?.[imprintStore.selectedImprintMethod?.sku]?.simple_array?.[calendarSku]?.weight ?? '',
            '_mainProductId': calendar.id,
            '_groupedCalendarId': appStore.mainProductID,
            '_price_details': correctedPriceDetails,  // ✅ Use corrected price details
            '_productUrl': window.location.pathname,
            '_runningFeePerItem': backendConfig.running_fee || 0,
            '_groupID': groupID,           // ✅ Add groupID for cart association
            '_qtyIncrement': qtyIncrement  // ✅ Add qtyIncrement for cart qty controls
        };
        
        // Add imprint properties (merge all fields from imprintProperties except _price_details)
        Object.keys(imprintProperties).forEach(key => {
            if (key !== '_price_details') {
                properties[key] = imprintProperties[key];
            }
        });
        
        // Build item
        const item = {
            id: calendar.id,
            quantity: calendar.qty,
            properties: properties,
            metaData: {
                price: (calendar.unitPrice || 0).toFixed(2),
                productColorID: calendar.id,
                imgID: null,
                imprint_sku: imprintStore.selectedImprintMethod?.sku || '',
                mainProduct_id: calendar.id
            }
        };
        
        items.push(item);
    });
    
    const payload = {
        id: appStore.mainProductID,
        colorProductID: calendarStore.products[0]?.id || appStore.mainProductID,
        imprint_lines: imprintLines,  // ✅ Use extracted imprint_lines
        items: items
    };
    
    console.log('[PayloadBuilder] Calendar payload imprint_lines:', imprintLines);
    
    console.log('[PayloadBuilder] Calendar payload built:', payload);
    return payload;
}

/**
 * Combine multiple imprints into single payload (Variant A: Prefixed properties)
 * Each imprint gets a number suffix: (1), (2), (3), etc.
 * Properties are grouped by imprint for better readability
 *
 * @param {Array} allImprintsPayloads - Array of {imprintKey, payload} objects
 * @returns {Object} Combined payload with prefixed properties
 */
/**
* Combine multiple imprints into single payload (Variant A: Prefixed properties)
* Each imprint gets a number suffix: (1), (2), (3), etc.
* Properties are grouped by imprint for better readability
*
* @param {Array} allImprintsPayloads - Array of {imprintKey, payload} objects
* @returns {Object} Combined payload with prefixed properties
*/
export function combineMultipleImprintsAsPrefixed(allImprintsPayloads) {
    if (!allImprintsPayloads || allImprintsPayloads.length === 0) {
        console.error('[PayloadBuilder] No imprints to combine');
        return null;
    }

    console.log('[PayloadBuilder] Combining', allImprintsPayloads.length, 'imprints with prefixes');

    // Use array to maintain order, then convert to object
    const propertiesArray = [];
    const combinedImprintLines = {};
    let combinedFees = null;

    // Process each imprint sequentially for grouped output
    allImprintsPayloads.forEach((item, index) => {
        const imprintNum = index + 1;
        const suffix = index === 0 ? '' : ` (${imprintNum})`;
        const suffixUnderscore = index === 0 ? '' : `_${imprintNum}`;
        const orderPrefix = String(imprintNum).padStart(3, '0'); // 001, 002, 003...

        const props = item.payload.properties;

        console.log(`[PayloadBuilder] Processing imprint ${imprintNum}:`, {
            location: props["Imprint location"],
            method: props["Imprint Method"]
        });

        // GROUP 1: Location and Method (for this imprint)
        propertiesArray.push({
            order: `${orderPrefix}_01`,
            key: `Imprint location${suffix}`,
            value: props["Imprint location"]
        });
        propertiesArray.push({
            order: `${orderPrefix}_02`,
            key: `_Imprint Location SKU${suffixUnderscore}`,
            value: props["_Imprint Location SKU"]
        });
        propertiesArray.push({
            order: `${orderPrefix}_03`,
            key: `Imprint Method${suffix}`,
            value: props["Imprint Method"]
        });
        propertiesArray.push({
            order: `${orderPrefix}_04`,
            key: `_Imprint Method SKU${suffixUnderscore}`,
            value: props["_Imprint Method SKU"]
        });

        // GROUP 2: Text lines (for this imprint)
        const lineKeys = Object.keys(props)
            .filter(key => key.startsWith('Line ') && key.includes(' text'))
            .sort((a, b) => {
                const numA = parseInt(a.match(/Line (\d+)/)[1]);
                const numB = parseInt(b.match(/Line (\d+)/)[1]);
                return numA - numB;
            });

        lineKeys.forEach((key, lineIndex) => {
            const lineMatch = key.match(/Line (\d+) text/);
            if (lineMatch) {
                const lineNum = lineMatch[1];
                const lineOrderBase = 10 + (lineIndex * 3); // 10, 13, 16, 19...

                // Add line text
                propertiesArray.push({
                    order: `${orderPrefix}_${String(lineOrderBase).padStart(2, '0')}`,
                    key: `Line ${lineNum} text${suffix}`,
                    value: props[key]
                });

                // Add line color (right after text)
                const colorKey = `Line ${lineNum} color`;
                if (props[colorKey]) {
                    propertiesArray.push({
                        order: `${orderPrefix}_${String(lineOrderBase + 1).padStart(2, '0')}`,
                        key: `Line ${lineNum} color${suffix}`,
                        value: props[colorKey]
                    });
                }

                // Add color SKU (right after color)
                const colorSkuKey = `_Imprint Line ${lineNum} Color SKU`;
                if (props[colorSkuKey]) {
                    propertiesArray.push({
                        order: `${orderPrefix}_${String(lineOrderBase + 2).padStart(2, '0')}`,
                        key: `_Imprint Line ${lineNum} Color SKU${suffixUnderscore}`,
                        value: props[colorSkuKey]
                    });
                }
            }
        });

        // GROUP 3: Image data (for this imprint)
        if (props["Add an Image Filename"]) {
            const imageOrderBase = 70;

            propertiesArray.push({
                order: `${orderPrefix}_${imageOrderBase}`,
                key: `Add an Image Filename${suffix}`,
                value: props["Add an Image Filename"]
            });
            propertiesArray.push({
                order: `${orderPrefix}_${imageOrderBase + 1}`,
                key: `_Add an Image Url${suffixUnderscore}`,
                value: props["_Add an Image Url"]
            });

            if (props["Add an Image Color"]) {
                propertiesArray.push({
                    order: `${orderPrefix}_${imageOrderBase + 2}`,
                    key: `Add an Image Color${suffix}`,
                    value: props["Add an Image Color"]
                });
                propertiesArray.push({
                    order: `${orderPrefix}_${imageOrderBase + 3}`,
                    key: `_Add an Image Color SKU${suffixUnderscore}`,
                    value: props["_Add an Image Color SKU"]
                });
            }
        }

        // GROUP 4: Special instructions (for this imprint)
        if (props["Special Instruction"]) {
            propertiesArray.push({
                order: `${orderPrefix}_80`,
                key: `Special Instruction${suffix}`,
                value: props["Special Instruction"]
            });
        }

        // Combine imprint_lines with prefix
        const imprintLinePrefix = index === 0 ? '' : `imprint${imprintNum}_`;
        Object.keys(item.payload.imprint_lines).forEach(key => {
            combinedImprintLines[imprintLinePrefix + key] = item.payload.imprint_lines[key];
        });

        // Combine fees (sum setup, image, custom colour, and additional location fees)
        if (index === 0) {
            combinedFees = { ...item.payload.fees, additional_location_fee: 0 };
        } else {
            combinedFees.setup_fee = (combinedFees.setup_fee || 0) + (item.payload.fees?.setup_fee || 0);
            combinedFees.image_fee = (combinedFees.image_fee || 0) + (item.payload.fees?.image_fee || 0);
            combinedFees.customColourFee = (combinedFees.customColourFee || 0) + (item.payload.fees?.customColourFee || 0);
            // Additional location fee: index >= 1 means this is the 2nd+ location
            combinedFees.additional_location_fee = (combinedFees.additional_location_fee || 0) + (item.payload.fees?.additional_location_fee || 0);
        }
    });

    // Sort by order and convert to object
    propertiesArray.sort((a, b) => a.order.localeCompare(b.order));
    const combinedProperties = {};
    propertiesArray.forEach(item => {
        combinedProperties[item.key] = item.value;
    });

    // Add _price_details from first imprint (with combined fees)
    const firstPayload = allImprintsPayloads[0].payload;
    if (firstPayload.properties["_price_details"]) {
        combinedProperties["_price_details"] = {
            ...firstPayload.properties["_price_details"],
            setup_fee: combinedFees.setup_fee,
            image_fee: combinedFees.image_fee,
            customColourFee: combinedFees.customColourFee
        };
    }

    // Add other common properties at the end
    if (firstPayload.properties["_colorUpcharge"]) {
        combinedProperties["_colorUpcharge"] = firstPayload.properties["_colorUpcharge"];
    }
    if (firstPayload.properties["_sizeUpcharge"]) {
        combinedProperties["_sizeUpcharge"] = firstPayload.properties["_sizeUpcharge"];
    }

    // Add imprints count
    combinedProperties["_imprints_count"] = allImprintsPayloads.length;

    console.log('[PayloadBuilder] Combined', allImprintsPayloads.length, 'imprints');
    console.log('[PayloadBuilder] Combined fees:', combinedFees);

    return {
        imprint_lines: combinedImprintLines,
        properties: combinedProperties,
        fees: combinedFees
    };
}

/**
 * Helper: Extract line data from properties
 */
function extractLinesFromProperties(properties) {
    const lines = [];
    let lineNum = 1;

    while (properties[`Line ${lineNum} text`]) {
        lines.push({
            [`Line ${lineNum} text`]: properties[`Line ${lineNum} text`],
            [`Line ${lineNum} color`]: properties[`Line ${lineNum} color`],
            [`Line ${lineNum} Color SKU`]: properties[`_Imprint Line ${lineNum} Color SKU`]
        });
        lineNum++;
    }

    return lines;
}

/**
 * Helper: Extract image data from properties
 */
function extractImageDataFromProperties(properties) {
    if (!properties["Add an Image Filename"]) {
        return null;
    }

    return {
        filename: properties["Add an Image Filename"],
        url: properties["_Add an Image Url"],
        "Image Color": properties["Add an Image Color"],
        "Image Color SKU": properties["_Add an Image Color SKU"]
    };
}

export function combineMultipleImprintsAsArray(allImprintsPayloads, feesArray = []) {
    if (!allImprintsPayloads || allImprintsPayloads.length === 0) {
        return null;
    }

    console.log('[PayloadBuilder] combineMultipleImprintsAsArray - processing', allImprintsPayloads.length, 'imprints');

    // Get first imprint as base
    const firstPayload = allImprintsPayloads[0].payload;

    // Build imprints array
    const imprintsArray = allImprintsPayloads.map(item => ({
        imprintKey: item.imprintKey,
        'Imprint location': item.payload.properties["Imprint location"],
        "_Imprint Location SKU": item.payload.properties["_Imprint Location SKU"],
        "Imprint Method": item.payload.properties["Imprint Method"],
        "_Imprint Method SKU": item.payload.properties["_Imprint Method SKU"],
        lines: extractLinesFromProperties(item.payload.properties),
        // "Special Instructions": item.payload.properties["Special Instruction"],
        "Image Data": extractImageDataFromProperties(item.payload.properties)
    }));

    // ✅ NEW STRUCTURE: Build imprint_lines as ARRAY of objects
    // Each object in array = one imprint with its lines
    const imprintLinesArray = allImprintsPayloads.map((item, index) => {
        const imprintLines = item.payload.imprint_lines;
        console.log(`[PayloadBuilder] Imprint ${index} lines:`, imprintLines);
        return imprintLines; // Each imprint's lines as separate object in array
    });

    console.log('[PayloadBuilder] Final imprint_lines array:', imprintLinesArray);

    // Build combined properties
    const properties = {
        ...firstPayload.properties,
        "_Imprint Lines": imprintsArray,
        "_Imprints Count": allImprintsPayloads.length
    };

    // Combine fees (sum setup fees, keep other fees from first)
    const totalSetupFee = allImprintsPayloads.reduce((sum, item) => {
        return sum + (item.payload.fees?.setup_fee || 0);
    }, 0);
    const totalSetupFeeQty = allImprintsPayloads.reduce((sum, item) => {
        return sum + (item.payload.fees?.setup_fee_qty || 0);
    }, 0);
    const setupFeeUnitPrice = allImprintsPayloads[0]?.payload.fees?.setup_fee_unit_price || 0;

    // Proof fee is per-order — use combiner (takes from first location that triggers it)
    const proofFeeUnit = getFeeUnit(feesArray, 'PROOF', 'per order');
    const imprintsFeeObjects = allImprintsPayloads.map(item => item.payload.fees || {});
    const totalProofFee = combineProofFee(imprintsFeeObjects, proofFeeUnit);

    const totalRunningFee = allImprintsPayloads.reduce((sum, item) => {
        return sum + (item.payload.fees?.running_fee || 0);
    }, 0);
    const totalRunningFeeMultiplier = allImprintsPayloads.reduce((sum, item) => {
        return sum + (item.payload.fees?.running_fee_multiplier || 0);
    }, 0);
    const runningFeeRate = allImprintsPayloads[0]?.payload.fees?.running_fee_rate || 0;

    const totalImageFee = allImprintsPayloads.reduce((sum, item) => {
        return sum + (item.payload.fees?.image_fee || 0);
    }, 0);
    const totalImageFeeQty = allImprintsPayloads.reduce((sum, item) => {
        return sum + (item.payload.fees?.image_fee_qty || 0);
    }, 0);
    const imageFeeUnitPrice = allImprintsPayloads[0]?.payload.fees?.image_fee_unit_price || 0;

    const totalCustomColourFee = allImprintsPayloads.reduce((sum, item) => {
        return sum + (item.payload.fees?.customColourFee || 0);
    }, 0);
    const totalCustomColourFeeQty = allImprintsPayloads.reduce((sum, item) => {
        return sum + (item.payload.fees?.customColourFee_qty || 0);
    }, 0);
    const customColourFeeUnitPrice = allImprintsPayloads[0]?.payload.fees?.customColourFee_unit_price || 0;

    // Additional Location Fee — applies from 2nd imprint onward, once per extra location
    const totalAdditionalLocationFee = allImprintsPayloads.reduce((sum, item, index) => {
        if (index === 0) return sum; // first location is free
        return sum + (item.payload.fees?.additional_location_fee || 0);
    }, 0);
    const totalAdditionalLocationFeeQty = allImprintsPayloads.reduce((sum, item, index) => {
        if (index === 0) return sum;
        return sum + (item.payload.fees?.additional_location_fee_qty || 0);
    }, 0);
    const additionalLocationFeeUnitPrice = allImprintsPayloads.length > 1
        ? (allImprintsPayloads[1]?.payload.fees?.additional_location_fee_unit_price || 0)
        : 0;

    // additional_location_fee tiers — pass the full tier array so cart.js can recalculate
    // fee_per_piece dynamically based on the current qty (instead of a static snapshot)
    const additionalLocationFeeTiers = allImprintsPayloads.length > 1
        ? (allImprintsPayloads[1]?.payload?.fees?.additional_location_fee_tiers || [])
        : [];

    console.log('[PayloadBuilder] Combined fees:', {
        totalSetupFee,
        totalProofFee,
        totalRunningFee,
        totalImageFee,
        totalCustomColourFee,
        totalAdditionalLocationFee
    });

    // Update _price_details with combined fees
    if (properties["_price_details"]) {
        properties["_price_details"].setup_fee = totalSetupFee;
        properties["_price_details"].setup_fee_qty = totalSetupFeeQty;
        properties["_price_details"].setup_fee_unit_price = setupFeeUnitPrice;
        properties["_price_details"].proof_fee = totalProofFee;
        properties["_price_details"].running_fee = totalRunningFee;
        properties["_price_details"].running_fee_multiplier = totalRunningFeeMultiplier;
        properties["_price_details"].running_fee_rate = runningFeeRate;
        properties["_price_details"].image_fee = totalImageFee;
        properties["_price_details"].image_fee_qty = totalImageFeeQty;
        properties["_price_details"].image_fee_unit_price = imageFeeUnitPrice;
        properties["_price_details"].customColourFee = totalCustomColourFee;
        properties["_price_details"].customColourFee_qty = totalCustomColourFeeQty;
        properties["_price_details"].customColourFee_unit_price = customColourFeeUnitPrice;
        properties["_price_details"].additional_location_fee = totalAdditionalLocationFee;
        properties["_price_details"].additional_location_fee_qty = totalAdditionalLocationFeeQty;
        properties["_price_details"].additional_location_fee_unit_price = additionalLocationFeeUnitPrice;
        // Full tier array so cart.fees.js can recalculate fee_per_piece dynamically by current qty
        properties["_price_details"].additional_location_fee_tiers = additionalLocationFeeTiers;
    }

    return {
        imprint_lines: imprintLinesArray,  // ✅ ARRAY of objects instead of single object
        properties: properties,
        fees: {
            ...firstPayload.fees,
            setup_fee: totalSetupFee,
            setup_fee_qty: totalSetupFeeQty,
            setup_fee_unit_price: setupFeeUnitPrice,
            proof_fee: totalProofFee,
            running_fee: totalRunningFee,
            running_fee_multiplier: totalRunningFeeMultiplier,
            running_fee_rate: runningFeeRate,
            image_fee: totalImageFee,
            image_fee_qty: totalImageFeeQty,
            image_fee_unit_price: imageFeeUnitPrice,
            customColourFee: totalCustomColourFee,
            customColourFee_qty: totalCustomColourFeeQty,
            customColourFee_unit_price: customColourFeeUnitPrice,
            additional_location_fee: totalAdditionalLocationFee,
            additional_location_fee_qty: totalAdditionalLocationFeeQty,
            additional_location_fee_unit_price: additionalLocationFeeUnitPrice
        }
    };
}

/*
* Get special instructions from last step review add to cart
* */
function getSpecialInstructions(){
    const selectionStore= useSelectionStore();
    return  selectionStore.specialInstructions || '';
}